import type { ReactElement } from "react";
import React from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import api from "./api";
import Dashboard from "./pages/Dashboard";
import { EndUserLicenseAgreement, PrivacyPolicy } from "./pages/LegalPages";
import PollResponsePage from "./pages/PollResponsePage";
import PushpayCallback from "./pages/PushpayCallback";
import Reports from "./pages/Reports";
import StrategyMapEditor from "./pages/StrategyMapEditor";
import Users from "./pages/Users";

type AppUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  enterprise_id?: number | null;
  is_active?: boolean;
};

const authTokenStorageKey = "lenses.authToken";
const authUserStorageKey = "lenses.authUser";

const readCachedAuthUser = (): AppUser | null => {
  if (typeof window === "undefined") return null;
  try {
    const rawUser = window.localStorage.getItem(authUserStorageKey);
    if (!rawUser) return null;
    const parsed = JSON.parse(rawUser) as Partial<AppUser>;
    if (
      typeof parsed.id !== "number" ||
      typeof parsed.name !== "string" ||
      typeof parsed.email !== "string" ||
      typeof parsed.role !== "string"
    ) {
      return null;
    }
    return {
      id: parsed.id,
      name: parsed.name,
      email: parsed.email,
      role: parsed.role,
      enterprise_id: parsed.enterprise_id ?? null,
      is_active: parsed.is_active ?? true,
    };
  } catch {
    window.localStorage.removeItem(authUserStorageKey);
    return null;
  }
};

const cacheAuthUser = (user: AppUser) => {
  window.localStorage.setItem(authUserStorageKey, JSON.stringify(user));
};

const clearAuthSession = () => {
  window.localStorage.removeItem(authTokenStorageKey);
  window.localStorage.removeItem(authUserStorageKey);
};

function RequireAuth({
  children,
  render,
}: {
  children?: ReactElement;
  render?: (currentUser: AppUser | null, onSignOut: () => void) => ReactElement;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const token =
    typeof window === "undefined" ? "" : window.localStorage.getItem(authTokenStorageKey);
  const initialCachedUser = readCachedAuthUser();
  const [isChecking, setIsChecking] = React.useState(() => Boolean(token && !initialCachedUser));
  const [isAuthenticated, setIsAuthenticated] = React.useState(() => Boolean(token && initialCachedUser));
  const [currentUser, setCurrentUser] = React.useState<AppUser | null>(() => initialCachedUser);
  const currentUserRef = React.useRef<AppUser | null>(initialCachedUser);

  React.useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  React.useEffect(() => {
    let isActive = true;

    const checkSession = async () => {
      if (!token) {
        setIsAuthenticated(false);
        setIsChecking(false);
        return;
      }

      try {
        if (!currentUserRef.current) {
          setIsChecking(true);
        }
        const response = await api.get("/auth/me");
        if (isActive) {
          setIsAuthenticated(true);
          const nextUser = response.data as AppUser;
          setCurrentUser(nextUser);
          cacheAuthUser(nextUser);
        }
      } catch (error) {
        clearAuthSession();
        if (isActive) {
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      } finally {
        if (isActive) {
          setIsChecking(false);
        }
      }
    };

    if (!currentUserRef.current) {
      setIsChecking(true);
    }
    void checkSession();

    return () => {
      isActive = false;
    };
  }, [token]);

  if (isChecking) {
    return <div style={{ padding: 28, fontWeight: 800 }}>Checking sign in...</div>;
  }

  if (!token || !isAuthenticated) {
    const nextPath = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/users?next=${encodeURIComponent(nextPath)}`} replace />;
  }

  const onSignOut = () => {
    void api
      .post("/auth/logout", undefined, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .catch(() => undefined);
    clearAuthSession();
    setIsAuthenticated(false);
    setCurrentUser(null);
    navigate("/users", { replace: true });
  };

  return (
    <>
      {render ? (
        render(currentUser, onSignOut)
      ) : (
        <>
          <div style={accountBarStyle} aria-label="Signed in account">
            <div style={{ minWidth: 0 }}>
              <div style={accountNameStyle}>{currentUser?.name || "Signed in"}</div>
              <div style={accountEmailStyle}>{currentUser?.email || currentUser?.role || ""}</div>
            </div>
            <button type="button" onClick={onSignOut} style={signOutButtonStyle}>
              Sign out
            </button>
          </div>
          {children}
        </>
      )}
    </>
  );
}

function HashRouteRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const hashPath = location.hash.startsWith("#/") ? location.hash.slice(1) : "";

  React.useEffect(() => {
    if (!hashPath) return;
    navigate(hashPath, { replace: true });
  }, [hashPath, navigate]);

  return null;
}

export default function App() {
  return (
    <Router>
      <HashRouteRedirect />
      <Routes>
        <Route
          path="/"
          element={
            <RequireAuth
              render={(currentUser, onSignOut) => (
                <StrategyMapEditor signedInUser={currentUser} onSignOut={onSignOut} />
              )}
            />
          }
        />
        <Route
          path="/reports"
          element={
            <RequireAuth
              render={(currentUser, onSignOut) => (
                <Reports signedInUser={currentUser} onSignOut={onSignOut} />
              )}
            />
          }
        />
        <Route
          path="/dashboard"
          element={
            <RequireAuth
              render={(currentUser, onSignOut) => (
                <Dashboard signedInUser={currentUser} onSignOut={onSignOut} />
              )}
            />
          }
        />
        <Route path="/users" element={<Users />} />
        <Route path="/integrations/pushpay/callback" element={<PushpayCallback />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/end-user-license-agreement" element={<EndUserLicenseAgreement />} />
        <Route path="/polls/respond" element={<PollResponsePage />} />
        <Route path="/polls/respond/:enterpriseId/:inviteToken" element={<PollResponsePage />} />
      </Routes>
    </Router>
  );
}

const accountBarStyle: React.CSSProperties = {
  position: "fixed",
  right: 16,
  top: 16,
  zIndex: 10000,
  display: "flex",
  alignItems: "center",
  gap: 8,
  maxWidth: "min(320px, calc(100vw - 32px))",
  border: "1px solid rgba(15, 23, 42, 0.14)",
  borderRadius: 8,
  padding: "7px 8px",
  background: "rgba(255, 255, 255, 0.94)",
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.14)",
  color: "#111827",
  fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const accountNameStyle: React.CSSProperties = {
  maxWidth: 150,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: 12,
  fontWeight: 800,
};

const accountEmailStyle: React.CSSProperties = {
  maxWidth: 150,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: 10,
  color: "#64748b",
};

const signOutButtonStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: 7,
  padding: "5px 7px",
  background: "#fff",
  color: "#111827",
  cursor: "pointer",
  fontSize: 11,
  fontWeight: 800,
  whiteSpace: "nowrap",
};
